import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { Col, Row, Skeleton } from 'antd'
import { Formik } from 'formik'
import { type } from 'os'
import * as Yup from 'yup'

import getCannedMessageById from 'core/application/docReviewDashboard/cannedMessages/getCannedMessageById'
import {
  CannedMessage,
  CannedMessageType,
} from 'core/application/docReviewDashboard/cannedMessages/getForCannedMessagesView'
import upsertCannedMessage from 'core/application/docReviewDashboard/cannedMessages/upsertCannedMessage'

import Option from 'common/components/dataEntry/components/Option'
import Input from 'common/components/dataEntry/formik/FormikDefaultInputField'
import HtmlEditor from 'common/components/dataEntry/formik/FormikHtmlEditorField'
import Select from 'common/components/dataEntry/formik/FormikSelectField'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'

import { commandRequest, queryRequest } from 'common/RequestUtils'
import { ROUTE_PATHS } from 'common/routePaths'

import './addCannedMessages.less'

const formValidator = (translate: any) =>
  Yup.object({
    title: Yup.string().trim().required(translate('general.required')),
    message: Yup.string().trim().required(translate('general.required')),
    type: Yup.number().required(translate('general.required')),
  })

export interface CompleteCannedMessageFormValue {
  id?: string
  title?: string
  message?: string
  type?: string
}

export type SelectOption = {
  id: any
  name: any
}

let defaultCannedMessageFormValues: CompleteCannedMessageFormValue = {
  title: '',
  message: '',
  type: '',
}

const AddCannedMessages: React.FC = () => {
  let { cannedMessageId } = useParams<any>()
  const { t: translate } = useTranslation()
  let history = useHistory()
  const [initialValues, setInitialValues] = useState<CompleteCannedMessageFormValue>(defaultCannedMessageFormValues)
  const [loading, setLoading] = useState<boolean>(false)

  const items = [
    { id: 0, name: 'Expense' },
    { id: 1, name: 'Premium' },
  ]

  const getInitialData = async (data: CompleteCannedMessageFormValue) => {
    let initial: CompleteCannedMessageFormValue
    initial = cannedMessageId
      ? {
          id: cannedMessageId,
          title: data.title,
          message: data.message,
          type: data.type.toString(),
        }
      : {}

    setInitialValues(initial)
  }

  const fetchData = async () => {
    setLoading(true)
    if (cannedMessageId) {
      let data = await queryRequest(() => getCannedMessageById(cannedMessageId))
      await getInitialData(data)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const submitData = async (values: CompleteCannedMessageFormValue) => {
    let dataForSubmit: CannedMessage = {
      id: values.id,
      title: values.title ?? '',
      message: values.message ?? '',
      type: parseInt(values.type) ?? null,
    }

    let result = await commandRequest(() => upsertCannedMessage(dataForSubmit))

    if (!result.errors) {
      notification.open({
        message: translate('doc-review-dashboard.canned-messages.add-success-message'),
        type: 'success',
      })
      history.push(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/canned-messages`)
    } else {
      notification.open({
        message: result.errors[0],
        type: 'error',
      })
      return Promise.reject()
    }
  }

  return (
    <div className="super-wrapper">
      <Row className="inner-padding">
        <Col span={4} className="text-semi-bold">
          {cannedMessageId
            ? translate('doc-review-dashboard.canned-messages.edit-message-title')
            : translate('doc-review-dashboard.canned-messages.add-message-title')}
        </Col>
      </Row>
      <Skeleton loading={loading}>
        {!loading && (
          <Formik
            enableReinitialize={true}
            validationSchema={formValidator(translate)}
            initialValues={initialValues}
            onSubmit={submitData}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <Row className="inner-padding">
                  <Col span={12}>
                    <Input name="title" label="Title" placeholder="Title of message" />
                  </Col>
                </Row>
                <Row className="inner-padding">
                  <Col span={12}>
                    <Select
                      name="type"
                      label="Type"
                      placeholder="Type"
                      defaultValue={[
                        initialValues.type === '0' ? items[0].name : initialValues.type === '1' ? items[1].name : '',
                      ]}
                    >
                      {items.map((item: SelectOption) => (
                        <Option key={item.id} value={item.name}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
                <Row className="inner-padding">
                  <Col span={12}>
                    <HtmlEditor name="message" value={initialValues.message ?? ''} label="Message" />
                  </Col>
                </Row>
                <Row className="inner-padding">
                  <Col>
                    <Button
                      htmlType="submit"
                      disabled={!formValidator(translate).isValidSync(formik.values)}
                      type="primary"
                    >
                      {translate('general.save')}
                    </Button>
                    <Button
                      onClick={() => {
                        history.push(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/canned-messages`)
                      }}
                      className="ml-16"
                      type="default"
                    >
                      {translate('general.cancel')}
                    </Button>
                  </Col>
                </Row>
              </form>
            )}
          </Formik>
        )}
      </Skeleton>
    </div>
  )
}

export default AddCannedMessages
